import React from "react";
import { Link } from "react-router-dom";
import SecondaryButton from "../components/Buttons/SecondaryButton";

export interface OrganizationToDelete {
  id: string;
  name: string;
}

interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  deletePassword: string;
  setDeletePassword: (val: string) => void;
  deleteError: string;
  deleteLoading: boolean;
  organizationsToDelete: OrganizationToDelete[];
  loadingOrganizations: boolean;
  isPsdiUser?: boolean;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  open,
  onClose,
  onSubmit,
  deletePassword,
  setDeletePassword,
  deleteError,
  deleteLoading,
  organizationsToDelete,
  loadingOrganizations,
  isPsdiUser = false,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 max-[600px]:px-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md mt-[120px] max-[600px]:mt-[80px]">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h5 className="text-lg font-semibold text-red-600">Delete Account</h5>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600"
            onClick={onClose}
            disabled={deleteLoading}
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="px-6 py-4">
            <p className="mb-4">
              Are you sure you want to delete your account? This action cannot
              be undone.
            </p>
            <p className="mb-4 text-gray-700">This will permanently delete:</p>
            <ul className="list-disc list-inside mb-4 text-gray-700">
              <li>Your profile and account data</li>
              <li>Your personal documents and pages</li>
              <li>All associated data from our systems</li>
            </ul>
            <p className="mb-4 text-gray-700 text-sm">
              <strong>Note:</strong> Documents associated with organisations
              will be preserved and remain accessible to organisation members.
            </p>
            {loadingOrganizations && (
              <div className="rounded-md border border-gray-300 bg-gray-50 p-3 text-gray-700 mb-4">
                <p className="text-sm">Loading organisations...</p>
              </div>
            )}
            {!loadingOrganizations && organizationsToDelete.length > 0 && (
              <div className="rounded-md border border-red-300 bg-red-50 p-3 text-red-800 mb-4">
                <p className="font-semibold mb-2 text-sm">
                  Cannot delete account
                </p>
                <p className="text-sm mb-2">
                  You are the only admin of the following organisation(s):
                </p>
                <ul className="list-disc list-inside text-sm mb-2">
                  {organizationsToDelete.map((org) => (
                    <li key={org.id}>{org.name}</li>
                  ))}
                </ul>
                <p className="text-sm">
                  Please contact us if you want to delete your account.{" "}
                </p>
              </div>
            )}
            <div className="rounded-md border border-yellow-400 bg-yellow-50 p-3 text-yellow-800">
              <small>
                Some data may stay in backups or logs as part of our data
                retention policy for legal and security reasons. You can read
                the full details in our{" "}
                <Link
                  to="/legal/privacy-policy"
                  className="text-blue-600 hover:text-blue-700"
                >
                  Privacy Policy
                </Link>
                .
              </small>
            </div>
            <div className="mb-3 mt-4">
              {!isPsdiUser && (
                <>
                  <label
                    htmlFor="deletePassword"
                    className="block mb-2 font-medium"
                  >
                    Enter your password to confirm
                  </label>
                  <input
                    type="password"
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                    id="deletePassword"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Enter your password"
                    disabled={deleteLoading}
                  />
                </>
              )}
            </div>
            {deleteError && (
              <div
                className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-3"
                role="alert"
              >
                {deleteError}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t px-6 py-4">
            <SecondaryButton
              type="button"
              onClick={onClose}
              disabled={deleteLoading}
            >
              Cancel
            </SecondaryButton>
            <button
              type="submit"
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              disabled={deleteLoading || organizationsToDelete.length > 0}
            >
              {deleteLoading ? "Deleting..." : "Delete Account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
